import React from 'react';
import './StatusActionButton.css';

export interface StatusActionButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  icon?: string;
  loadingIcon?: string;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  // Дополнительные пропсы для стилизации
  margin?: string;
  width?: string;
  title?: string;
}

const StatusActionButton: React.FC<StatusActionButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  disabled = false,
  loading = false,
  icon,
  loadingIcon = 'ri-loader-4-line ri-spin',
  className = '',
  type = 'button',
  margin,
  width,
  title
}) => {
  const handledByPointerRef = React.useRef(false);
  const pressStartRef = React.useRef<{ x: number; y: number } | null>(null);
  const lastActionTimeRef = React.useRef<number>(0);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (disabled || loading) return;
    pressStartRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (disabled || loading) return;
    const t = e.touches[0];
    pressStartRef.current = { x: t.clientX, y: t.clientY };
  };

  const performActionIfAllowed = () => {
    const now = Date.now();
    if (now - lastActionTimeRef.current < 350) return;
    lastActionTimeRef.current = now;
    handledByPointerRef.current = true;
    // Instrumentation: measure handler duration (supports async)
    try {
      const labelText = typeof children === 'string' ? children : (title || 'StatusActionButton');
      const timerLabel = `StatusActionButton:${labelText}:${now}`;
      console.debug(`[Button] start ${timerLabel}`);
      console.time(timerLabel);
      const res = onClick?.();
      if (res && typeof (res as any).then === 'function') {
        (res as Promise<any>).then(() => {
          console.timeEnd(timerLabel);
          console.debug(`[Button] end ${timerLabel}`);
        }).catch((err) => {
          console.timeEnd(timerLabel);
          console.error(`[Button] error ${timerLabel}`, err);
        });
      } else {
        console.timeEnd(timerLabel);
        console.debug(`[Button] end ${timerLabel}`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setTimeout(() => { handledByPointerRef.current = false; }, 300);
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (disabled || loading) return;
    const start = pressStartRef.current;
    pressStartRef.current = null;
    if (!start) {
      // nothing to do here; final action will be triggered on click
      return;
    }
    const dx = Math.abs(e.clientX - start.x);
    const dy = Math.abs(e.clientY - start.y);
    if (Math.sqrt(dx * dx + dy * dy) > 10) return; // treated as scroll/drag
    // do not perform action here — handleClick will run it on 'click' phase
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (disabled || loading) return;
    const start = pressStartRef.current;
    pressStartRef.current = null;
    const touch = e.changedTouches[0];
    if (!start) {
      // nothing to do here; final action will be triggered on 'click'
      return;
    }
    const dx = Math.abs((touch?.clientX || 0) - start.x);
    const dy = Math.abs((touch?.clientY || 0) - start.y);
    if (Math.sqrt(dx * dx + dy * dy) > 10) return;
    // do not perform action here — handleClick will run it on 'click' phase
  };

  const handleClick = (e: React.MouseEvent) => {
    if (handledByPointerRef.current) {
      // If already handled by pointer (rare), suppress duplicate click.
      e.preventDefault();
      e.stopPropagation();
      handledByPointerRef.current = false;
      return;
    }
    // Execute action in click phase to avoid click-through after unmount.
    performActionIfAllowed();
  };

  const buttonClasses = [
    'status-action-btn',
    `status-action-btn--${variant}`,
    `status-action-btn--${size}`,
    fullWidth ? 'status-action-btn--full-width' : '',
    disabled ? 'status-action-btn--disabled' : '',
    loading ? 'status-action-btn--loading' : '',
    className
  ].filter(Boolean).join(' ');

  const buttonStyle: React.CSSProperties = {
    ...(margin && { margin }),
    ...(width && { width })
  };

  return (
    <button
      type={type}
      className={buttonClasses}
      style={buttonStyle}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      disabled={disabled || loading}
      title={title}
    >
      {loading ? (
        <i className={loadingIcon}></i>
      ) : icon ? (
        <i className={icon}></i>
      ) : null}
      <span>{children}</span>
    </button>
  );
};

export default StatusActionButton;
